import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Edit, Trash2, RefreshCw, Shield, AlertTriangle, Lock, Users } from 'lucide-react';
import { Role } from '@/services/rolesPermissionsService';

interface RoleCardProps {
  role: Role;
  onEdit: (role: Role) => void;
  onDelete: (roleId: number) => void;
  onManagePermissions: (role: Role) => void;
}

export const RoleCard: React.FC<RoleCardProps> = ({
  role,
  onEdit,
  onDelete,
  onManagePermissions
}) => {
  return (
    <Card className="border-l-4" style={{ borderLeftColor: role.color || '#6b7280' }}>
      <CardHeader>
        <div className="flex justify-between items-start gap-2">
          <div className="min-w-0 flex-1">
            <CardTitle className="text-lg break-words">
              {role.display_name || role.name}
            </CardTitle>
            {role.display_name && (
              <p className="text-xs text-muted-foreground font-mono mt-1 break-all">
                {role.name}
              </p>
            )}
            {role.is_protected && (
              <Badge variant="secondary" className="w-fit mt-2 bg-red-50 text-red-700 border-red-200">
                <Lock className="w-3 h-3 mr-1" />
                Protegido
              </Badge>
            )}
          </div>
          <div className="flex shrink-0 gap-1">
            <Button
              size="icon"
              variant="ghost"
              onClick={() => onManagePermissions(role)}
              title="Gestionar permisos"
            >
              <RefreshCw className="w-4 h-4" />
            </Button>
            <Button
              size="icon"
              variant="ghost"
              onClick={() => onEdit(role)}
            >
              <Edit className="w-4 h-4" />
            </Button>
            <Button
              size="icon"
              variant="ghost"
              onClick={() => onDelete(role.id)}
              disabled={role.is_protected}
              title={role.is_protected ? 'Este rol está protegido y no puede ser eliminado' : 'Eliminar rol'}
            >
              <Trash2 className={`w-4 h-4 ${role.is_protected ? 'text-gray-400' : ''}`} />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-muted-foreground mb-3">
          {role.description || 'Sin descripción'}
        </p>
        <div className="space-y-2">
          {/* Contador de permisos */}
          <div className="flex items-center gap-2 text-sm">
            {(!role.permissions || role.permissions.length === 0) ? (
              <>
                <AlertTriangle className="w-4 h-4 text-amber-500" />
                <span className="text-muted-foreground">Sin permisos asignados</span>
              </>
            ) : (
              <>
                <Shield className="w-4 h-4 text-blue-500" />
                <span className="font-medium">
                  {role.permissions.length}
                  {role.permissions.length === 1 ? ' permiso asignado' : ' permisos asignados'}
                </span>
              </>
            )}
          </div>
          
          {/* Contador de usuarios */}
          <div className="flex items-center gap-2 text-sm">
            <Users className="w-4 h-4 text-green-500" />
            <span className="font-medium">
              {role.users_count ?? 0}
              {(role.users_count ?? 0) === 1 ? ' usuario asignado' : ' usuarios asignados'}
            </span>
            {(role.users_count ?? 0) === 0 && (
              <span className="text-muted-foreground">• Este rol no está siendo usado</span>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};